import { DesktopDatePicker as MuiDesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ReactComponent as ArrowDownIcon } from 'assett/icons/arrow_down_icon.svg';
import { ReactComponent as ArrowleftIcon } from 'assett/icons/arrow_left_datepicker_icon.svg';
import { ReactComponent as ArrowRightIcon } from 'assett/icons/arrow_right_datepicker_icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assett/icons/arrow_up_icon.svg';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import styled from 'styled-components';
import Stack from 'styles/stack/Stack';
import Typography from 'styles/typography/Typograpy';
import { useTheme } from 'stylesHooks';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import getMonthRangeStyles from './getMonthRangeStyles';

interface IMonthRangePicker {
	name: string;
	dateStart: Dayjs | null;
	dateEnd: Dayjs | null;
	error?: boolean;
	errorMessage?: string;
	setStartDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
	setEndDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
	onClose?: () => void;
}

const PAPER_WIDTH = '300px';
const PAPER_HEIGHT = '315px';

const paperBaseSx = {
	width: `${PAPER_WIDTH}!important`,
	height: PAPER_HEIGHT,
	'& .MuiPickersLayout-root': { width: `${PAPER_WIDTH}!important` },
	'& .MuiDateCalendar-root': {
		width: `${PAPER_WIDTH}!important`,
		height: PAPER_HEIGHT,
	},
	'& .MuiMonthCalendar-root': {
		width: '100%',
		margin: 0,
		marginTop: '20px',
		padding: 0,
		gap: '20px 0px',
	},
	'& .MuiPickersMonth-root': {
		flexBasis: '33.33%',
		padding: 0,
		margin: 0,
		height: '32px',
		border: 'none',
	},
};

const getMonthButtonSx = (primaryColor: string) => ({
	'& .MuiPickersMonth-monthButton': {
		width: '100%',
		height: '32px',
		borderRadius: '16px',
		border: '2px solid transparent',
		margin: 0,
		padding: 0,
		backgroundColor: 'white',
		'&:hover': { border: `2px solid ${primaryColor}` },
		'&.Mui-selected': {
			backgroundColor: 'white!important',
			color: `${primaryColor}!important`,
			border: '2px solid transparent',
		},
	},
});

const MonthRangePicker = ({
	name,
	dateStart,
	dateEnd,
	error,
	errorMessage,
	setStartDate,
	setEndDate,
	onClose,
}: IMonthRangePicker) => {
	const [open, setOpen] = useState(false);
	const [viewYear, setViewYear] = useState(dayjs().year());
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const theme = useTheme();
	const primaryColor = theme.palette.primary.main;
	const errorColor = theme.palette.error.main;

	const shrinkLabel = open || (!!dateStart && !!dateEnd) || error;

	const handleOpenPicker = () => setOpen(true);

	const handleClosePicker = () => {
		setOpen(false);
		onClose?.();
	};

	const onSelectMonth = (selected: Dayjs) => {
		const month = selected.startOf('month');

		// Both dates set or neither: start fresh selection
		if ((dateStart && dateEnd) || (!dateStart && !dateEnd)) {
			setStartDate(month);
			setEndDate(null);
			return;
		}

		// Only start date set: determine end
		if (dateStart && !dateEnd) {
			if (month.isSame(dateStart, 'month')) {
				setStartDate(month);
				setEndDate(null);
			} else if (month.isAfter(dateStart, 'month')) {
				setEndDate(month);
				handleClosePicker();
			} else {
				setEndDate(dateStart);
				setStartDate(month);
				handleClosePicker();
			}
		}
	};

	const renderInputValue = () => {
		if (dateStart && dateEnd)
			return `${dateStart.format('MMM YYYY')} - ${dateEnd.format('MMM YYYY')}`;
		if (dateStart) return dateStart.format('MMM YYYY');
		return '';
	};

	const monthRangeStyles = getMonthRangeStyles(dateStart, dateEnd, viewYear);

	return (
		<Stack position="relative">
			<DesktopDatePicker
				open={open}
				onOpen={handleOpenPicker}
				onClose={handleClosePicker}
				label={tClientLabels(`commons.datePickerHeader.${name}`)}
				onChange={() => {}}
				value={null}
				views={['year', 'month']}
				openTo="month"
				yearsPerRow={3}
				closeOnSelect={false}
				disableHighlightToday
				onYearChange={((year: Dayjs) => setViewYear(year.year())) as any}
				onMonthChange={
					((month: Dayjs) => {
						setViewYear(month.year());
						onSelectMonth(month);
					}) as any
				}
				slots={{
					openPickerIcon: () =>
						open ? <ArrowUpIcon /> : <ArrowDownIcon onClick={handleOpenPicker} />,
					leftArrowIcon: () => <ArrowleftIcon />,
					rightArrowIcon: () => <ArrowRightIcon />,
				}}
				slotProps={{
					popper: {
						sx: {
							width: PAPER_WIDTH,
							background: 'transparent',
							transform: 'translate(50px, 10px)',
						},
					},
					desktopPaper: {
						elevation: 0,
						style: { transform: 'translate(0px, 5px)' },
						sx: {
							...paperBaseSx,
							...getMonthButtonSx(primaryColor),
							...monthRangeStyles,
						},
					},
					textField: {
						onClick: handleOpenPicker,
						fullWidth: true,
						variant: 'filled' as const,
						InputLabelProps: {
							shrink: shrinkLabel,
							sx: {
								color: error ? `${errorColor}!important` : '',
								'&.Mui-focused': { color: error ? `${errorColor}!important` : '' },
							},
						},
						inputProps: {
							sx: { cursor: 'pointer' },
							value: renderInputValue(),
							placeholder: 'mmm yyyy - mmm yyyy',
							readOnly: true,
						},
						InputProps: {
							sx: { borderColor: error ? `${errorColor}!important` : '' },
						},
					},
				}}
			/>
			{error && errorMessage && (
				<Typography
					variant="error"
					sx={{ position: 'absolute', bottom: '-22px', left: '0' }}
				>
					{tClientLabels(errorMessage)}
				</Typography>
			)}
		</Stack>
	);
};

const DesktopDatePicker = styled(MuiDesktopDatePicker)`
	width: 100%;
`;

export default MonthRangePicker;
